import withKycRequired from "@rentiohq/shared-frontend/dist/hoc/withKycRequired";
import * as bankAccountHooks from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.hooks";
import * as paymentRequestApi from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.api";
import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import {
  EPaymentMethod,
  EPaymentRepetitionType,
  IBankWirePayment,
} from "@rentiohq/shared-frontend/dist/types/payment.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatIban } from "@rentiohq/shared-frontend/dist/utils/iban.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import * as paymentRequestUtils from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import { htmlTextAsReactText } from "@rentiohq/shared-frontend/dist/utils/text/text.utils";
import {
  Box,
  Button,
  MainCard,
  Typography,
  Divider,
  Stack,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { valuesForPaymentRequest } from "../../Payments.utils";
import { PaymentSuccess } from "../PaymentSuccess";
import { BankWireStyles } from "./BankWireOverview.styles";

interface IProps {
  paymentMethod: EPaymentMethod;
}

const BankWireOverviewBase = ({ paymentMethod }: IProps) => {
  const sx = BankWireStyles();
  const params = useParams();
  const navigate = useNavigate();

  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const [bankWirePayment, setBankWirePayment] = React.useState<
    IBankWirePayment | undefined
  >(undefined);
  const [fetchBankWireError, setFetchBankWireError] = React.useState<
    Error | undefined
  >(undefined);
  const [isFetchingBankWire, setIsFetchingBankWire] = React.useState(false);
  const [didCompleteBankWire, setDidCompleteBankWire] = React.useState(false);

  const {
    detail: paymentRequest,
    fetchError: paymentRequestError,
    isFetching: isFetchingPaymentRequest,
  } = paymentRequestHooks.useDetail({
    id: paymentRequestId,
    shouldRefetch: false,
  });

  const {
    detail: property,
    fetchError: propertyFetchError,
    isFetching: isFetchingProperty,
  } = propertyHooks.useDetail({
    id: paymentRequest?.requestPropertyId,
    shouldRefetch: false,
  });

  const {
    detail: ibanisationBankAccount,
    isFetching: isFetchingIbanisationBankAccount,
    fetchError: fetchIbanisationError,
    // refetch: fetchIbanisationBankAccount,
  } = bankAccountHooks.useDetail({
    shouldRefetch: false,
    skipLegacy: true,
    customPath:
      paymentRequestId && paymentMethod === EPaymentMethod.Ibanisation
        ? `/payment-requests/${paymentRequestId}/ibanisation-bank-account`
        : undefined,
  });

  const fetchBankWire = async () => {
    if (!paymentRequestId) {
      return;
    }

    try {
      setIsFetchingBankWire(true);

      const { data } =
        await paymentRequestApi.generateBankWirePayment(paymentRequestId);

      setIsFetchingBankWire(false);
      setBankWirePayment(data);
    } catch (unknownError) {
      const error = unknownError as any;

      setIsFetchingBankWire(false);
      setFetchBankWireError(error);
    }
  };

  React.useEffect(() => {
    if (paymentMethod === EPaymentMethod.BankWire) {
      fetchBankWire();
    }
  }, []);

  React.useEffect(() => {
    const apiError =
      paymentRequestError ||
      fetchIbanisationError ||
      propertyFetchError ||
      fetchBankWireError;
    if (apiError?.message) {
      enqueueSnackbar({
        message: apiError?.message,
        variant: "error",
      });
    }
  }, [
    paymentRequestError,
    fetchIbanisationError,
    propertyFetchError,
    fetchBankWireError,
  ]);

  const handlePressPaid = async () => {
    setDidCompleteBankWire(true);
  };

  const renderListItem = (data: any) => {
    return (
      <Stack direction="row" gap={2} alignItems="baseline" key={data.label}>
        <Box>
          <Typography
            textTransform="uppercase"
            variant="body1"
            sx={sx.content.infoLabel}
          >
            {data.label}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="body1"
            sx={sx.content.infoValue(data.highlighted)}
          >
            {data.value}
          </Typography>
          {data.info && (
            <Typography variant="caption" sx={sx.content.infoSubValue}>
              {data.info}
            </Typography>
          )}
        </Box>
      </Stack>
    );
  };

  const renderBankWire = () => {
    if (!bankWirePayment || !paymentRequest) {
      return null;
    }

    const {
      bankName,
      bankAddress,
      bankAccount,
      bankBic,
      reference,
      // qrContent,
    } = bankWirePayment.metaData;
    const { amount } = paymentRequest;

    const infoList = [
      {
        label: getLocalizedText("payment.online.bank_wire.info.recipient"),
        value: bankName,
        info: getLocalizedText("payment.online.bank_wire.info.recipient.extra"),
      },
      {
        label: getLocalizedText(
          "payment.online.bank_wire.info.recipient_address",
        ),
        value: bankAddress,
      },
      {
        label: getLocalizedText("payment.online.bank_wire.info.bank_account"),
        value: formatIban(bankAccount),
      },
      {
        label: getLocalizedText("payment.online.bank_wire.info.bank_bic"),
        value: bankBic,
      },
      {
        label: getLocalizedText("payment.online.bank_wire.info.amount"),
        value: amount ? formatCurrency(amount) : undefined,
      },
      {
        label: getLocalizedText("payment.online.bank_wire.info.reference"),
        value: reference,
        highlighted: true,
      },
    ];

    return infoList
      .filter(data => !!data.value)
      .map(data => renderListItem(data));
  };

  const renderIbanisation = () => {
    if (!ibanisationBankAccount || !paymentRequest) {
      return null;
    }

    const { bank, iban, mangoWalletIbanBic } = ibanisationBankAccount;
    const { amount, ibanisationReference } = paymentRequest;
    const infoList = [
      {
        label: getLocalizedText("payment.online.bank_wire.info.recipient"),
        value:
          bank ||
          getLocalizedText("payment.online.bank_wire.info.mangopay.bank_name"),
        info: getLocalizedText("payment.online.bank_wire.info.recipient.extra"),
      },
      {
        label: getLocalizedText(
          "payment.online.bank_wire.info.recipient_address",
        ),
        value: getLocalizedText(
          "payment.online.bank_wire.info.mangopay.bank_address",
        ),
      },
      {
        label: getLocalizedText("payment.online.bank_wire.info.bank_account"),
        value: formatIban(iban),
      },
      {
        label: getLocalizedText("payment.online.bank_wire.info.bank_bic"),
        value: mangoWalletIbanBic,
      },
      {
        label: getLocalizedText("payment.online.bank_wire.info.amount"),
        value: amount ? formatCurrency(amount) : undefined,
      },
      {
        label: getLocalizedText("payment.online.bank_wire.info.reference"),
        value: ibanisationReference,
        highlighted: true,
      },
    ];

    return infoList
      .filter(data => !!data.value)
      .map(data => renderListItem(data));
  };

  if (didCompleteBankWire) {
    return <PaymentSuccess />;
  }

  const isFetching =
    isFetchingProperty ||
    isFetchingPaymentRequest ||
    isFetchingIbanisationBankAccount ||
    isFetchingBankWire;
  return (
    <MainCard content={false}>
      <Box py={2} px={3}>
        {paymentRequest && (
          <Typography variant="h4" sx={sx.content.heading}>
            {getLocalizedText("payment.online.title.bank_wire", {
              paymentType: paymentRequestUtils.getTitle(paymentRequest),
            })}
          </Typography>
        )}
        {paymentRequest && (
          <Typography variant="caption">
            {htmlTextAsReactText(
              getLocalizedText(
                "tenant.payments.select_payment_method.subtitle",
                valuesForPaymentRequest(paymentRequest, property),
              ),
            )}
          </Typography>
        )}
      </Box>
      <Divider />
      <Box p={3}>
        <Stack direction="column" gap={1.5}>
          <Typography variant="h5" sx={sx.content.heading}>
            {getLocalizedText("payment.online.bank_wire.info.title")}
          </Typography>
          <Typography variant="subtitle1">
            {getLocalizedText(
              `payment.online.bank_wire.info.subtitle.ibanisation.${
                paymentRequest?.repetitionType === EPaymentRepetitionType.Once
                  ? "once"
                  : "recurring"
              }`,
            )}
          </Typography>
        </Stack>
        <Box>
          <Stack direction="column" gap={2} sx={sx.content.infoWrapper}>
            {paymentMethod === EPaymentMethod.BankWire &&
              paymentRequest?.allowedPaymentMethods.includes(
                EPaymentMethod.BankWire,
              ) &&
              renderBankWire()}

            {paymentMethod === EPaymentMethod.Ibanisation &&
              paymentRequest?.allowedPaymentMethods.includes(
                EPaymentMethod.Ibanisation,
              ) &&
              renderIbanisation()}
          </Stack>
        </Box>
        <Typography
          variant="body2"
          dangerouslySetInnerHTML={{
            __html: getLocalizedText("payment.online.bank_wire.info.extra"),
          }}
        />
        <Stack direction="row" gap={1} mt={2.5}>
          <Button
            variant={EButtonVariants.Outlined}
            sx={sx.content.actionBtn}
            onClick={() =>
              navigate(`/payment/${paymentRequestId}/payment-method`)
            }
          >
            {getLocalizedText("system.previous")}
          </Button>
          <Button
            variant={EButtonVariants.Contained}
            sx={sx.content.actionBtn}
            onClick={handlePressPaid}
            disabled={isFetching}
          >
            {getLocalizedText("payment_detail.cta.bank_wire_done")}
          </Button>
        </Stack>
      </Box>
    </MainCard>
  );
};

export const BankWireOverview = withKycRequired(BankWireOverviewBase);
